import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

import { api } from '../services/api';
import { fieldSectionSx } from './deviceConfigStyles';

const PROTOS = ['udp', 'tcp'];

const EMPTY = { ip: '', port: '', proto: 'udp', log_remote: '' };

const valuesEqual = (a, b) =>
  a.ip === b.ip && a.port === b.port && a.proto === b.proto && a.log_remote === b.log_remote;

const SyslogSection = ({ serial, isJnet2 }) => {
  const [cfg, setCfg] = useState(EMPTY);
  const [original, setOriginal] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const fetchCfg = useCallback(async () => {
    if (!serial || !isJnet2) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getSyslogConfig(serial);
      const s = data?.syslog_server || {};
      const vals = {
        ip: s.ip || '',
        port: s.port || '',
        proto: s.proto || 'udp',
        log_remote: s.log_remote || '',
      };
      setCfg(vals);
      setOriginal(vals);
    } catch (err) {
      setError(err?.message || 'Failed to load syslog config');
    } finally {
      setLoading(false);
    }
  }, [serial, isJnet2]);

  useEffect(() => {
    fetchCfg();
  }, [fetchCfg]);

  const dirty = useMemo(() => !valuesEqual(cfg, original), [cfg, original]);

  const handleChange = (field, value) => {
    setCfg((prev) => ({ ...prev, [field]: value }));
    if (status) setStatus(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await api.setSyslogConfig(serial, cfg);
      setOriginal(cfg);
      setStatus({ type: 'success', message: 'Syslog configuration saved' });
    } catch (err) {
      setStatus({ type: 'error', message: err?.message || 'Failed to save syslog configuration' });
    } finally {
      setSaving(false);
    }
  };

  if (!isJnet2) return null;

  return (
    <Paper variant="outlined" sx={fieldSectionSx}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Syslog Server
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchCfg}
            disabled={loading || saving}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!dirty || saving || loading}
          >
            Save
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {status && (
        <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
          {status.message}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="IP Address"
              value={cfg.ip}
              onChange={(e) => handleChange('ip', e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g. 192.168.1.100"
            />
            <TextField
              label="Port"
              value={cfg.port}
              onChange={(e) => handleChange('port', e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
              placeholder="e.g. 514"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Protocol</InputLabel>
              <Select
                value={cfg.proto || 'udp'}
                label="Protocol"
                onChange={(e) => handleChange('proto', e.target.value)}
              >
                {PROTOS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Remote Logging</InputLabel>
              <Select
                value={cfg.log_remote || ''}
                label="Remote Logging"
                onChange={(e) => handleChange('log_remote', e.target.value)}
              >
                <MenuItem value="">Off</MenuItem>
                <MenuItem value="1">On</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

export default SyslogSection;
